import {
  Box,
  Button,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { COLORS } from "colors/color";
import ChipInput from "components/Chips/chip";
import dayjs from "dayjs";
import React, { useState } from "react";
import { FieldTypography } from "StyledTypography/StyledTypography";
import CloseIcon from "@mui/icons-material/Close";
import { CancelButton } from "components/Buttons/Buttons";
import { NextButton } from "components/Buttons/Buttons";
import { InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import colors from "colors/color";
import { NewCancelButton } from "components/Buttons/Buttons";
import { NewCreateOfferButton } from "components/Buttons/Buttons";
import { Google, Microsoft } from "@mui/icons-material";
import axios from "axios";
import PropTypes from "prop-types";
import TextEditor2 from "components/TextFields/TextEditor2";
import SimpleEditor from "components/TextFields/SimpleEditor";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const theme = createTheme();
const apiUrl = process.env.REACT_APP_API_URL;
const apiUrlFront = process.env.REACT_APP_API_FRONT_URL;
const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "15px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    width: "700px", // Fixed width for the dialog
    maxWidth: "700px", // Ensures the dialog doesn't exceed this width
  },
});

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: "Poppins",
    borderColor: "#00000033",
    backgroundColor: "#fff",
    transition: "border-color 0.4s ease, box-shadow 0.4s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      borderColor: "#0017E7",
      transition: "all 0.3s ease",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      borderColor: "#0017E7",
      transition: "all 0.3s ease",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "36px",
      padding: "8px 14px",
    },
  },
}));

const StyledFormControl = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    height: "50px",
    "& fieldset": {
      borderColor: "#e0e0e0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#0017E7",
      borderWidth: "1px",
    },
    "&:hover": {
      transition: "0.3s ease",
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
    },
    "&.Mui-focused": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      "& fieldset": {
        borderColor: "#0017E7",
        borderWidth: "2px",
      },
    },
    "&.Mui-error": {
      "&:hover, &.Mui-focused": {
        boxShadow: "none",
        borderColor: "#d32f2f",
      },
    },
    // Ensure border remains visible when value is selected
    "& .MuiSelect-select": {
      borderColor: "inherit",
    },
  },
});

const StyledDatePicker = styled(DatePicker)({
  "& .MuiOutlinedInput-root": {
    height: 50,
    borderRadius: "10px",
    fontFamily: "Poppins",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      borderColor: "#0017E7",
      transition: "all 0.3s ease",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      boxShadow: "0px 4px 20px rgba(0, 23, 231, 0.1)",
      borderColor: "#0017E7",
      transition: "all 0.3s ease",
    },
  },
});

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  "& .MuiSvgIcon-root": {
    fontSize: 20, // Reduced from 28 to 20
  },
  "&.Mui-checked": {
    color: colors.primary,
  },
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)({
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  margin: "4px",
  padding: "8px 16px",
  width: "100%",
  height: "50px", // Match other component heights
  display: "flex",
  alignItems: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: colors.primary,
    backgroundColor: "#f5f5f5",
  },
  "&.Mui-checked": {
    borderColor: colors.primary,
    backgroundColor: "#f0f7ff",
  },
});

function PostDetailsDialog({
  setOpen,
  postTitle = "",
  setPostTitle = () => {},
  reference,
  setReference,
  accountType,
  setAccountType,
  postDescription = "",
  setPostDescription = () => {},
  offerType = "",
  setOfferType = () => {},
  postRequirement = [],
  setPostRequirement = () => {},
  postDeadline = null,
  handleDateChange = () => {},
  handleNext = () => {},
  open = false,
  errors = {},
  setErrors = () => {},
  dateError = "",
  setDateError = () => {},
  recruiterEmail,
  setRecruiterEmail,
  setIsCheckedScrPost,
  isCheckedScr = false,
  setIsCheckedScr = () => {},
}) {
  const { t } = useTranslation();
  const [emailError, setEmailError] = useState("");

  // Internal state for reference and email if props are not provided
  const [internalReference, setInternalReference] = useState("");
  const [internalEmail, setInternalEmail] = useState("");
  const [internalAccountType, setInternalAccountType] = useState("");

  // Use either prop values or internal state
  const referenceValue =
    reference !== undefined ? reference : internalReference;
  const emailValue =
    recruiterEmail !== undefined ? recruiterEmail : internalEmail;
  const accountTypeValue =
    accountType !== undefined ? accountType : internalAccountType;

  const handleReferenceChange = (event) => {
    const value = event.target.value;
    if (setReference) {
      setReference(value);
    } else {
      setInternalReference(value);
    }
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setRecruiterEmail(value);
    console.log("recruiterEmail", value);
  };

  const handleAccountTypeChange = (event) => {
    const value = event.target.value;
    if (setAccountType) {
      setAccountType(value);
    } else {
      setInternalAccountType(value);
    }
  };

  const handleCheckboxChangeScr = (value) => {
    setIsCheckedScr(value);
    if (!value) {
      // Reset fields when switching to normal post
      if (setReference) {
        setReference("");
      } else {
        setInternalReference("");
      }
      if (setRecruiterEmail) {
        setRecruiterEmail("");
      } else {
        setInternalEmail("");
      }
      if (setAccountType) {
        setAccountType("");
      } else {
        setInternalAccountType("");
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <Box
        sx={{
          width: "100%",
          height: 60,
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: " 1px solid #D9D9D9",
          paddingLeft: "16px",
          paddingRight: "16px",
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 500,
            fontFamily: "Poppins",
            color: "#000",
          }}
        >
          {t("postDetailsDialog.title")}
        </DialogTitle>
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            mr: 3,
            color: "#000",
            border: "1px solid #D9D9D9",
            "&:hover": {
              color: "#000",
              backgroundColor: "#0084ff08",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <StyledDialogContent>
        <Stack spacing={2} sx={{ margin: '16px' }}>
          <Typography
            sx={{
              color: colors.primary,
              fontSize: 15,
              fontFamily: "Poppins",
              fontWeight: "600",
            }}
          >
            {t("postDetailsDialog.postDetails")}
          </Typography>
          <Grid item xs={12} md={12} sx={{ pr: 1, pl: 1 }}>
            <Typography
              sx={{
                color: "#0000004D",
                fontSize: "13px",
                fontFamily: "Poppins",
                fontWeight: 500,
              }}
            >
              {t("postDetailsDialog.postTitleLabel")}
            </Typography>
            <StyledTextField
              variant="outlined"
              value={postTitle}
              onChange={(event) => setPostTitle(event.target.value)}
              error={!!errors.postTitle}
              helperText={errors.postTitle}
              required
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "50px",
                },
              }}
            />
          </Grid>
          <Grid container sx={{ marginLeft: '8px', marginRight: '8px' }}>
            {/* Type de publication */}
            
            
            {/* Type offer */}
            <Grid item xs={12} md={4} sx={{ pr: 1, pl: 1 }}>
              <FieldTypography>
                {t("postDetailsDialog.offerTypeLabel")}
              </FieldTypography>
              <StyledFormControl fullWidth error={!!errors.offerType}>
                <Select
                  labelId="offer-type-label"
                  value={offerType}
                  onChange={(event) => setOfferType(event.target.value)}
                  required
                  sx={{
                    borderRadius: "8px",
                    height: "50px",
                  }}
                >
                  <MenuItem value="job">
                    {t("postDetailsDialog.jobOption")}
                  </MenuItem>
                  <MenuItem value="internship">
                    {t("postDetailsDialog.internshipOption")}
                  </MenuItem>
                </Select>
                {errors.offerType && (
                  <FormHelperText>{errors.offerType}</FormHelperText>
                )}
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} md={4} sx={{ pr: 1, pl: 1 }}>
            <FieldTypography>
              {t("postDetailsDialog.offerDeadlineLabel")}
            </FieldTypography>
            <Box sx={{ width: "100%" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  value={postDeadline ? dayjs(postDeadline) : null}
                  onChange={handleDateChange}
                  format="DD-MM-YYYY"
                  minDate={dayjs().add(1, "day")} // Restricts selection to tomorrow or later
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      error: Boolean(dateError), // Highlight field if there's an error
                      helperText: dateError || "", // Display error message
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ pr: 1, pl: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  marginBottom: 0.8,
                }}
              >
                <Typography
                  sx={{
                    color: "#0000004D",
                    fontSize: "13px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    
                  }}
                >
                  {t("postDetailsDialog.postTypeLabel")}
                </Typography>
                <Tooltip
                  title={
                    <Typography
                      sx={{
                        color: "#fff",
                        p: 1,
                        fontSize: "12px",
                        fontFamily: "Poppins",
                      }}
                    >
                      {t("postDetailsDialog.tooltipMessageType")}
                    </Typography>
                  }
                  placement="top"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{
                      color: "#0000004D",
                      fontSize: "16px",
                      cursor: "pointer",
                      "&:hover": {
                        color: "#0084ff",
                      },
                    }}
                  />
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "left",
                  gap: 0.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                  height: "50px",
                }}
              >
                <StyledFormControlLabel
                  control={
                    <StyledCheckbox
                      checked={isCheckedScr}
                      onChange={() => handleCheckboxChangeScr(!isCheckedScr)}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          color: isCheckedScr ? colors.primary : "#000",
                        }}
                      >
                        {t("postDetailsDialog.scrapedPost")}
                      </Typography>
                    </Box>
                  }
                  className={isCheckedScr ? "Mui-checked" : ""}
                />
              </Box>
            </Grid>
          </Grid>
          {isCheckedScr && (
            <>
              <Grid container spacing={2} sx={{ marginLeft: '8px', marginRight: '8px', marginTop: '8px' }}>
                {/* Reference */}
                <Grid item xs={12} md={4} sx={{ pr: 1, pl: 1 }}>
                <FieldTypography>
                  {t("postDetailsDialog.recruiterEmailLabel")}
                </FieldTypography>
                <StyledTextField
                  variant="outlined"
                  value={recruiterEmail}
                  onChange={handleEmailChange}
                  error={!!errors.recruiterEmail}
                  helperText={errors.recruiterEmail}
                  placeholder={
                    accountType === "g"
                      ? "example@gmail.com"
                      : "example@outlook.com"
                  }
                  required
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "50px",
                    },
                  }}
                />
              </Grid>
                
                
                {/* Account Type */}
                <Grid item xs={12} md={4} sx={{ pr: 1, pl: 1 }}>
                  <FieldTypography>
                    {t("postDetailsDialog.AccountTypeLabel")}
                  </FieldTypography>
                  <StyledFormControl fullWidth error={!!errors.accountType}>
                    <Select
                      labelId="account-type-label"
                      value={accountType}
                      onChange={(event) => setAccountType(event.target.value)}
                      required
                      sx={{
                        borderRadius: "8px",
                        height: "50px",
                      }}
                    >
                      <MenuItem value="g">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Google style={{ color: "#DB4437" }} />
                          Google Gmail
                        </Box>
                      </MenuItem>
                      <MenuItem value="m">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Microsoft style={{ color: "#00A1F1" }} />
                          Microsoft Outlook
                        </Box>
                      </MenuItem>
                    </Select>
                    {errors.accountType && (
                      <FormHelperText>{errors.accountType}</FormHelperText>
                    )}
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12} md={4} sx={{ pr: 1, pl: 1 }}>
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#0000004D",
                          fontSize: "13px",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                        }}
                      >
                        {t("postDetailsDialog.referenceLabel")}
                      </Typography>
                      <Tooltip
                        title={
                          <Typography
                            sx={{
                              color: "#fff",
                              p: 1,
                              fontSize: "12px",
                              fontFamily: "Poppins",
                            }}
                          >
                            {t("postDetailsDialog.tooltipMessage")}
                          </Typography>
                        }
                        placement="top"
                        arrow
                      >
                        <HelpOutlineIcon
                          sx={{
                            color: "#0000004D",
                            fontSize: "16px",
                            cursor: "pointer",
                            "&:hover": {
                              color: "#0084ff",
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <StyledTextField
                      variant="outlined"
                      value={reference}
                      onChange={(event) => setReference(event.target.value)}
                      error={!!errors.reference}
                      helperText={errors.reference}
                      required
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                
              </Grid>
              
            </>
          )}
           <Grid item xs={12} md={12} sx={{ pr: 1, pl: 1 }}>
            <FieldTypography>
              {t("postDetailsDialog.skillsLabel")}
              
                <Tooltip
                title={
                  <Typography
                    sx={{
                      color: "#fff",
                      p: 1,
                      fontSize: "12px",
                      fontFamily: "Poppins",
                    }}
                  >
                    Ajoutez des compétences (appuyez sur Entrée ou utilisez des virgules). Vous pouvez également coller une liste séparée par des virgules.
                  </Typography>
                }
                placement="top"
                arrow
              >
                <HelpOutlineIcon
                  sx={{
                    color: "#0000004D",
                    fontSize: "16px",
                    ml: 1,
                    cursor: "pointer",
                    "&:hover": {
                      color: "#0084ff",
                    },
                  }}
                />
              </Tooltip>
            </FieldTypography>

            <ChipInput
              requirement={postRequirement}
              setRequirement={setPostRequirement}
            />
          </Grid>
          <Grid item xs={12} md={12} sx={{ pr: 1, pl: 1 }}>
            <Typography
              sx={{
                color: "#0000004D",
                fontSize: "13px",
                fontFamily: "Poppins",
                fontWeight: 500,
              }}
            >
              {t("postDetailsDialog.postDescriptionLabel")}
            </Typography>
            <SimpleEditor
              value={postDescription}
              onChange={(content) => setPostDescription(content)}
              placeholder="Enter post description..."
              maxLength={10000}
              error={!!errors.postDescription}
              helperText={errors.postDescription}
              minHeight="500px"
            />
          </Grid>

         

          
        </Stack>
      </StyledDialogContent>
      <DialogActions sx={{ padding: 2, justifyContent: "flex-end", pr: 2 }}>
        <NewCancelButton onClick={() => setOpen(false)} variant="outlined">
          {t("postDetailsDialog.cancelButton")}
        </NewCancelButton>
        <NewCreateOfferButton variant="contained" onClick={handleNext}>
          {t("postDetailsDialog.nextButton")}
        </NewCreateOfferButton>
      </DialogActions>
    </StyledDialog>
  );
}

PostDetailsDialog.propTypes = {
  setOpen: PropTypes.func.isRequired,
  postTitle: PropTypes.string,
  setPostTitle: PropTypes.func,
  reference: PropTypes.string,
  setReference: PropTypes.func,
  accountType: PropTypes.string,
  setAccountType: PropTypes.func,
  postDescription: PropTypes.string,
  setPostDescription: PropTypes.func,
  offerType: PropTypes.string,
  setOfferType: PropTypes.func,
  postRequirement: PropTypes.array,
  setPostRequirement: PropTypes.func,
  postDeadline: PropTypes.object,
  handleDateChange: PropTypes.func,
  handleNext: PropTypes.func,
  open: PropTypes.bool,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  dateError: PropTypes.string,
  setDateError: PropTypes.func,
  recruiterEmail: PropTypes.string,
  setRecruiterEmail: PropTypes.func,
};

export default PostDetailsDialog;
